import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Zap, Save, Search, Package, User } from "lucide-react";

const ELETROS_PURPLE = "#2B1E4A";
const ELETROS_GOLD = "#F5B400";

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${checked ? "bg-[#5B2CA0]" : "bg-[#D9CFD6]"}`}>
      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

export default function AdminEletros() {
  const [cfg, setCfg] = useState(null);
  const [resellers, setResellers] = useState([]);
  const [q, setQ] = useState("");
  const [savingCfg, setSavingCfg] = useState(false);
  const [mp, setMp] = useState(null);
  const [mpToken, setMpToken] = useState("");
  const [mpBusy, setMpBusy] = useState(false);
  const [pending, setPending] = useState(null);

  const saveMp = async () => {
    setMpBusy(true);
    try {
      const body = { enabled: true };
      if (mpToken.trim()) body.access_token = mpToken.trim();
      const { data } = await api.put("/eletros/mp/config", body);
      setMp(data); setMpToken("");
      toast.success("Conta Mercado Pago Eletros salva");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setMpBusy(false); }
  };
  const testMp = async () => {
    setMpBusy(true);
    try {
      const { data } = await api.post("/eletros/mp/test");
      toast[data.valid ? "success" : "error"](data.message || (data.valid ? "Validada" : "Erro"));
      setMp((m) => ({ ...m, status: data.status }));
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setMpBusy(false); }
  };
  const toggleMp = async (v) => {
    try { const { data } = await api.put("/eletros/mp/config", { enabled: v }); setMp(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const load = async () => {
    const [c, r, mp, pp] = await Promise.all([
      api.get("/eletros/config"), api.get("/eletros/resellers"), api.get("/eletros/mp/config"),
      api.get("/eletros/reports/promissory-pending").catch(() => ({ data: null })),
    ]);
    setCfg(c.data); setResellers(r.data); setMp(mp.data); setPending(pp.data);
  };
  useEffect(() => { load(); }, []);

  const setAdmin = async (r, v) => {
    setResellers((prev) => prev.map((x) => (x.id === r.id ? { ...x, eletros_admin: v } : x)));
    try {
      await api.put(`/eletros/resellers/${r.id}/admin`, { eletros_admin: v });
      toast.success(v ? "Administrador Eletros concedido" : "Privilegio removido");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); load(); }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const { data } = await api.put("/eletros/config", {
        default_commission: Number(cfg.default_commission),
        responsibility_days: Number(cfg.responsibility_days),
        auto_compensate: !!cfg.auto_compensate,
        withdraw_min: Number(cfg.withdraw_min),
        withdraw_schedule: cfg.withdraw_schedule,
        withdraw_full_required: !!cfg.withdraw_full_required,
        commission_on_recovered: !!cfg.commission_on_recovered,
        delinquency_suspend_pct: Number(cfg.delinquency_suspend_pct),
        responsibility_alert_limit: Number(cfg.responsibility_alert_limit),
        default_max_installments: Number(cfg.default_max_installments),
      });
      setCfg(data);
      toast.success("Configuracao Eletros salva");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSavingCfg(false); }
  };

  const updateAccess = async (r, patch) => {
    const next = { ...r.eletros, ...patch };
    setResellers((prev) => prev.map((x) => (x.id === r.id ? { ...x, eletros: next } : x)));
    try {
      const { data } = await api.put(`/eletros/resellers/${r.id}/access`, patch);
      setResellers((prev) => prev.map((x) => (x.id === r.id ? { ...x, eletros: data } : x)));
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); load(); }
  };

  const filtered = resellers.filter((r) => (r.name || "").toLowerCase().includes(q.toLowerCase()) || (r.email || "").toLowerCase().includes(q.toLowerCase()));

  if (!cfg) return <div className="p-6 text-sm text-[#5C5A57]">Carregando...</div>;

  return (
    <div className="space-y-6 p-4 lg:p-8" data-testid="admin-eletros-page">
      {/* Header identidade Eletros */}
      <div className="flex items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${ELETROS_PURPLE}, #5B2CA0)` }}>
        <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: ELETROS_GOLD }}>
          <Zap size={26} className="text-[#2B1E4A]" />
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: ELETROS_GOLD }}>Eletros</div>
          <h1 className="font-display text-xl font-bold">Gestao da operacao Eletros</h1>
          <p className="text-[12px] text-white/70">Operacao separada da Cris Lingerie · comissoes, carteira e recebimentos independentes</p>
        </div>
        <Link to="/admin/eletros/compradores" data-testid="eletros-buyers-link" className="ml-auto flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20">
          <User size={16} /> Compradores
        </Link>
        <Link to="/admin/eletros/catalogo" data-testid="eletros-catalog-link" className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20">
          <Package size={16} /> Catalogo de produtos
        </Link>
      </div>

      {/* Card gerencial: Promissorias pendentes */}
      {pending && (
        <Link to="/admin/eletros/recebimentos" data-testid="eletros-pending-card"
          className="block rounded-2xl border border-[#F5B400] bg-white p-5 transition-shadow hover:shadow-md">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: "#C9960A" }}>Promissorias pendentes</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-display text-3xl font-bold text-brand-dark" data-testid="pending-total">{pending.total_pending}</span>
                <span className="text-sm text-[#8A7788]">de {pending.total_relevant} vendas · {pending.pct_pending}%</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full px-2.5 py-1 text-[11px] font-bold" style={{ background: "#FEF3C7", color: "#B45309" }} data-testid="pending-no-reminder">Sem lembrete: {pending.no_reminder}</span>
              <span className="rounded-full bg-[#F1ECF7] px-2.5 py-1 text-[11px] font-bold text-[#5B2CA0]" data-testid="pending-with-reminder">Com lembrete: {pending.with_reminder}</span>
              <span className="rounded-full bg-red-50 px-2.5 py-1 text-[11px] font-bold text-red-600" data-testid="pending-limit">Limite atingido: {pending.limit_reached}</span>
            </div>
          </div>
        </Link>
      )}

      {/* Conta Mercado Pago DEDICADA do Eletros */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="eletros-mp-card">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="font-display text-lg font-bold text-brand-dark">Conta Mercado Pago (Eletros)</h2>
            <p className="text-[12px] text-[#8A7788]">Conta exclusiva da operacao Eletros. Nunca usa a conta da revendedora nem da Cris Lingerie.</p>
          </div>
          <MpStatusBadge status={mp?.status} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto]">
          <input type="password" autoComplete="new-password" value={mpToken} onChange={(e) => setMpToken(e.target.value)}
            placeholder={mp?.configured ? `Token salvo (${mp.masked}) - digite para substituir` : "Cole o Access Token do Mercado Pago Eletros"}
            data-testid="eletros-mp-token" className="inp" />
          <button onClick={saveMp} disabled={mpBusy} data-testid="eletros-mp-save" className="rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: ELETROS_PURPLE }}>Salvar</button>
          <button onClick={testMp} disabled={mpBusy || !mp?.configured} data-testid="eletros-mp-test" className="rounded-xl border border-border px-4 py-2 text-sm font-semibold text-[#5B2CA0] disabled:opacity-50">Validar conexao</button>
        </div>
        {mp?.configured && (
          <label className="mt-3 flex items-center gap-2 text-sm text-brand-dark">
            <input type="checkbox" checked={!!mp.enabled} onChange={(e) => toggleMp(e.target.checked)} data-testid="eletros-mp-enabled" /> Integracao ativa
          </label>
        )}
      </div>

      {/* Config global */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="eletros-config-card">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-brand-dark">Configuracoes financeiras</h2>
          <button onClick={saveCfg} disabled={savingCfg} data-testid="eletros-save-config" className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: ELETROS_PURPLE }}>
            <Save size={16} /> {savingCfg ? "Salvando..." : "Salvar"}
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Comissao padrao (%)">
            <input type="number" step="0.5" value={Math.round(cfg.default_commission * 1000) / 10}
              onChange={(e) => setCfg({ ...cfg, default_commission: Number(e.target.value) / 100 })}
              data-testid="cfg-commission" className="w-full rounded-lg border border-border px-3 py-2" />
          </Field>
          <Field label="Dias p/ responsabilidade da revendedora">
            <input type="number" value={cfg.responsibility_days} onChange={(e) => setCfg({ ...cfg, responsibility_days: e.target.value })} data-testid="cfg-resp-days" className="w-full rounded-lg border border-border px-3 py-2" />
          </Field>
          <Field label="Valor minimo de saque (R$)">
            <input type="number" step="1" value={cfg.withdraw_min} onChange={(e) => setCfg({ ...cfg, withdraw_min: e.target.value })} data-testid="cfg-withdraw-min" className="w-full rounded-lg border border-border px-3 py-2" />
          </Field>
          <Field label="Maximo de parcelas (padrao)">
            <input type="number" value={cfg.default_max_installments} onChange={(e) => setCfg({ ...cfg, default_max_installments: e.target.value })} data-testid="cfg-max-inst" className="w-full rounded-lg border border-border px-3 py-2" />
          </Field>
          <Field label="Suspender vendas se inadimplencia > (%)">
            <input type="number" step="0.5" value={cfg.delinquency_suspend_pct} onChange={(e) => setCfg({ ...cfg, delinquency_suspend_pct: e.target.value })} data-testid="cfg-susp-pct" className="w-full rounded-lg border border-border px-3 py-2" />
          </Field>
          <Field label="Alerta de responsabilidade acima de (R$)">
            <input type="number" step="50" value={cfg.responsibility_alert_limit} onChange={(e) => setCfg({ ...cfg, responsibility_alert_limit: e.target.value })} data-testid="cfg-resp-alert" className="w-full rounded-lg border border-border px-3 py-2" />
          </Field>
        </div>

        <div className="mt-4 space-y-1 border-t border-border pt-4">
          <SwitchRow label="Compensar responsabilidades automaticamente com comissoes" checked={!!cfg.auto_compensate} onChange={(v) => setCfg({ ...cfg, auto_compensate: v })} testid="cfg-auto-compensate" />
          <SwitchRow label="Gerar comissao quando a cliente paga parcela ja assumida pela revendedora" checked={!!cfg.commission_on_recovered} onChange={(v) => setCfg({ ...cfg, commission_on_recovered: v })} testid="cfg-commission-recovered" />
          <SwitchRow label="Saque total obrigatorio (revendedora saca todo o saldo disponivel)" checked={!!cfg.withdraw_full_required} onChange={(v) => setCfg({ ...cfg, withdraw_full_required: v })} testid="cfg-withdraw-full" />
        </div>

        <div className="mt-4 border-t border-border pt-4">
          <div className="mb-2 text-[10px] uppercase tracking-widest text-[#9A8A98]">Janela de saque</div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={cfg.withdraw_schedule?.type || "weekly"} onChange={(e) => setCfg({ ...cfg, withdraw_schedule: { ...cfg.withdraw_schedule, type: e.target.value } })} data-testid="cfg-withdraw-type" className="rounded-lg border border-border px-3 py-2">
              <option value="weekly">Semanal (dia da semana)</option>
              <option value="monthly">Mensal / dias do mes</option>
            </select>
            {cfg.withdraw_schedule?.type === "monthly" ? (
              <input placeholder="Dias do mes ex: 5,20" value={(cfg.withdraw_schedule?.days || []).join(",")}
                onChange={(e) => setCfg({ ...cfg, withdraw_schedule: { ...cfg.withdraw_schedule, days: e.target.value.split(",").map((x) => parseInt(x.trim(), 10)).filter((n) => !isNaN(n)) } })}
                data-testid="cfg-withdraw-days" className="w-48 rounded-lg border border-border px-3 py-2" />
            ) : (
              <select value={cfg.withdraw_schedule?.weekday ?? 4} onChange={(e) => setCfg({ ...cfg, withdraw_schedule: { ...cfg.withdraw_schedule, weekday: Number(e.target.value) } })} data-testid="cfg-withdraw-weekday" className="rounded-lg border border-border px-3 py-2">
                {["Segunda", "Terca", "Quarta", "Quinta", "Sexta", "Sabado", "Domingo"].map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Acesso das revendedoras */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="eletros-resellers-card">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="font-display text-lg font-bold text-brand-dark">Acesso das revendedoras</h2>
          <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-1.5">
            <Search size={15} className="text-[#9A8A98]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..." data-testid="eletros-reseller-search" className="w-40 text-sm outline-none" />
          </div>
        </div>
        <div className="space-y-3">
          {filtered.length === 0 && <div className="py-6 text-center text-sm text-[#9A8A98]">Nenhuma revendedora.</div>}
          {filtered.map((r) => (
            <div key={r.id} className="rounded-xl border border-border p-3" data-testid={`eletros-reseller-${r.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-brand-dark">{r.name}</span>
                    {r.is_test && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700">Teste</span>}
                    {r.eletros.enabled && <span className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase text-white" style={{ background: ELETROS_PURPLE }}>Eletros</span>}
                  </div>
                  <div className="truncate text-[12px] text-[#8A7788]">{r.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-[#6B5A69]">{r.eletros.enabled ? "Habilitada" : "Desabilitada"}</span>
                  <Toggle checked={r.eletros.enabled} onChange={(v) => updateAccess(r, { enabled: v })} testid={`eletros-toggle-${r.id}`} />
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between gap-3 rounded-lg bg-[#FBFAFE] px-3 py-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-brand-dark">
                    Administrador Eletros
                    {r.eletros_admin && <span className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#2B1E4A]" style={{ background: ELETROS_GOLD }}>Ativo</span>}
                  </div>
                  <div className="text-[11px] text-[#8A7788]">Administra a operacao Eletros sem acesso as configuracoes estruturais nem a Cris Lingerie.</div>
                </div>
                <Toggle checked={!!r.eletros_admin} onChange={(v) => setAdmin(r, v)} testid={`eletros-admin-toggle-${r.id}`} />
              </div>
              {r.eletros.enabled && (
                <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3 sm:grid-cols-4">
                  <Field label="Comissao (%)" small>
                    <input type="number" step="0.5" placeholder={`padrao ${Math.round(cfg.default_commission * 1000) / 10}`}
                      value={r.eletros.commission_rate != null ? Math.round(r.eletros.commission_rate * 1000) / 10 : ""}
                      onChange={(e) => updateAccess(r, { commission_rate: e.target.value === "" ? null : Number(e.target.value) / 100 })}
                      data-testid={`eletros-comm-${r.id}`} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm" />
                  </Field>
                  <Field label="Limite carteira (R$)" small>
                    <input type="number" value={r.eletros.wallet_limit ?? ""} onChange={(e) => updateAccess(r, { wallet_limit: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`eletros-limit-${r.id}`} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm" />
                  </Field>
                  <Field label="Max. parcelas" small>
                    <input type="number" value={r.eletros.max_installments} onChange={(e) => updateAccess(r, { max_installments: Number(e.target.value) })} data-testid={`eletros-maxinst-${r.id}`} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm" />
                  </Field>
                  <Field label="Status" small>
                    <select value={r.eletros.status} onChange={(e) => updateAccess(r, { status: e.target.value })} data-testid={`eletros-status-${r.id}`} className="w-full rounded-lg border border-border px-2 py-1.5 text-sm">
                      <option value="ativa">Ativa</option>
                      <option value="suspensa">Suspensa</option>
                      <option value="bloqueada">Bloqueada</option>
                    </select>
                  </Field>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, small }) {
  return (
    <label className="block">
      <span className={`mb-1 block ${small ? "text-[10px]" : "text-[11px]"} uppercase tracking-widest text-[#9A8A98]`}>{label}</span>
      {children}
    </label>
  );
}

function SwitchRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-[14px] text-brand-dark">{label}</span>
      <Toggle checked={checked} onChange={onChange} testid={testid} />
    </div>
  );
}

function MpStatusBadge({ status }) {
  const map = {
    nao_configurada: { t: "Nao configurada", c: "bg-gray-100 text-gray-600" },
    configurada: { t: "Configurada", c: "bg-amber-100 text-amber-700" },
    validada: { t: "Validada", c: "bg-emerald-100 text-emerald-700" },
    erro: { t: "Erro", c: "bg-red-100 text-red-700" },
  };
  const s = map[status] || map.nao_configurada;
  return <span data-testid="eletros-mp-status" className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${s.c}`}>{s.t}</span>;
}
